/**
 * OSMDExerciseScene - scene expander (MUS-317, Phase 1 "7b").
 *
 * Expands one playable take of the Exercise Section's `stepwise_run` recipe -
 * (recipe prompt x cell coordinate x frontier configuration) - into BOTH the
 * notation (a MusicXML string the scene feeds to OSMDPlayableScene) and the
 * judged-note stream, from ONE computation, per the MUS-317 consumption
 * contract (documentation/exercise-section/05-content-authoring.md section 1).
 *
 * What this owes (MUS-317):
 * - Interval-walk expansion from the recipe's `intervals` (carried as data, not
 *   hardcoded formulas): the traversal is recipe-driven, and key spelling is
 *   derived from the scale itself (not the figure), so a different figure over the
 *   same scale is a different interval list rather than new expansion code.
 * - Laid out like the engraved reference for the major-scale runs
 *   (OSMDExerciseScene.runScore.ts): register by the range, the left hand one
 *   octave below the right, bars of 8 with a held final tonic on runs up and
 *   down, and staves by the hand with whole spans crossing to the other staff.
 * - Honest key spelling: notes are spelled by the cell's scale formula in the
 *   tonic's key (F# in G major, never Gb; F natural in D Dorian), under the key
 *   signature the scale's family is written with (scaleNotation.ts: Major its
 *   own, a mode its parent major's, harmonic and melodic minor the tonic's minor
 *   key, every other scale none), or none at all when the user shows
 *   accidentals only. A cell whose tonic label would write a theoretical key is
 *   written from the enharmonic tonic (Db minor as C# minor); its pitches are
 *   the same.
 * - The spelling rides on each pitch's step and alter only. Whether an
 *   accidental is drawn is left to the renderer, which reads the key signature
 *   and the bar (OSMD draws every <accidental> element it is given, even one
 *   the key signature already covers, so none is emitted).
 * - Chord-capable output path: notes carry a midi ARRAY (this figure is melodic,
 *   one pitch per event, but the pipeline never bakes in one-pitch-per-event).
 *
 * - Finger numbers: the reference fingering (scaleFingering.ts) on every note,
 *   display only, when the user shows them; none where no fingering is authored.
 * No mid-run clef changes are ever emitted.
 *
 * This module is PURE (no Phaser, no OSMD, no React) so the music-theory logic
 * is unit-tested in isolation. The scene binds it to the session machine.
 */

import type { StepwiseRunPrompt } from 'Types/ExerciseRecipeTypes';
import type { Direction, Hand, ScaleCell, ScaleConfig, Tonic } from 'Types/ExerciseContentTypes';
import { unitIdForScaleSlug } from 'Seeds/ExerciseContent/scaleLevels';
import {
  DEFAULT_KEY_SIGNATURE_DISPLAY,
  scaleNotation,
  type KeySignatureDisplay,
} from 'Seeds/ExerciseContent/scaleNotation';
import { spellScale, type NoteLetter, type SpelledNote } from 'Seeds/ExerciseContent/scaleSpelling';
import {
  DEFAULT_FINGER_NUMBERS_DISPLAY,
  fingeringFor,
  runFingers,
  type Finger,
  type FingerNumbersDisplay,
} from 'Seeds/ExerciseContent/scaleFingering';
import {
  runRegister,
  runTiming,
  writeRunScore,
  type RunHand,
  type RunLine,
  type RunScoreNote,
  type RunStaff,
} from './OSMDExerciseScene.runScore';

/** One expanded, judgeable note of the run (chord-capable via `midiNotes`). */
export interface JudgedNote {
  /** Position in the emitted judged-note stream (staff-flattened). */
  index: number;
  /** The pitch(es) sounding at this note. Melodic here => length 1. */
  midiNotes: number[];
  /** Which hand plays it (whichever staff it is written on). */
  hand: 'RH' | 'LH';
  /** The staff it is written on: 1 treble, 2 bass. */
  staff: RunStaff;
  /** Absolute onset in MusicXML input divisions (DIVISIONS=4 per quarter). */
  startDivisions: number;
  /** Note length in the same division space (quarter=4, eighth=2, 16th=1). */
  durationDivisions: number;
  /** The reference finger shown on the note (display only, never graded); none where unknown. */
  finger?: number;
}

/** The single-computation output: notation + the judged stream that mirrors it. */
export interface ExpandedRun {
  /** MusicXML of the whole run, its lines broken by new-system. */
  musicXml: string;
  /** Each line as its own score, for the scene to show as one phrase a line. */
  lines: RunLine[];
  /** Bars the notation occupies. */
  measureCount: number;
  /** The judged-note stream, ordered by (onset, RH-before-LH). */
  judgedNotes: JudgedNote[];
  /** The meter the transport runs the run in (a full bar's); never drawn. */
  timeSignature: [number, number];
  /** Each hand's first tonic, as a MIDI pitch. */
  register: { rhBase: number; lhBase: number };
}

/** How the run is drawn: the user's notation display settings. */
export interface ExpanderNotationOptions {
  keySignature?: KeySignatureDisplay;
  fingerNumbers?: FingerNumbersDisplay;
}

/** rhythm_density -> note length in DIVISIONS=4 input space. */
const RHYTHM_DIVISIONS: Record<ScaleConfig['rhythm_density'], number> = {
  quarter: 4,
  eighth: 2,
  sixteenth: 1,
};

const SEMITONES_PER_OCTAVE = 12;

const pitchClassOf = (midi: number): number =>
  ((midi % SEMITONES_PER_OCTAVE) + SEMITONES_PER_OCTAVE) % SEMITONES_PER_OCTAVE;

/**
 * The ascending semitone offsets of the walk (relative to the base tonic), for
 * `range` octaves of `stepIntervals`. Ends on the top tonic (offset 12*range).
 */
const ascendingOffsets = (stepIntervals: number[], rangeOctaves: number): number[] => {
  const steps = rangeOctaves * stepIntervals.length;
  return Array.from({ length: steps }).reduce<number[]>(
    (offsets, _unused, k) => [
      ...offsets,
      offsets[offsets.length - 1] + stepIntervals[k % stepIntervals.length],
    ],
    [0],
  );
};

/** The ascending walk's indexes in the configured direction. */
const directedIndexes = (count: number, direction: Direction): number[] => {
  const ascending = Array.from({ length: count }, (_unused, index) => index);
  if (direction === 'asc') return ascending;
  if (direction === 'desc') return [...ascending].reverse();
  // 'both': asc then back down without doubling the top note, ending on the tonic.
  return [...ascending, ...ascending.slice(0, -1).reverse()];
};

const handsToRender = (hand: Hand): RunHand[] => (hand === 'HT' ? ['RH', 'LH'] : [hand]);

/** A spelled pitch: the scale note on that pitch class, or its plain sharp name off the scale. */
const spellingByPitchClass = (notes: readonly SpelledNote[]): Map<number, SpelledNote> =>
  new Map(notes.map((note) => [note.pitchClass, note]));

/**
 * One hand's fingers along the ascending walk (one per note, bottom tonic to top), or null when
 * the reference table has none for that hand. A descending note keeps its ascending finger.
 */
const ascendingFingers = (
  unitId: string,
  tonic: Tonic,
  hand: RunHand,
  rangeOctaves: number,
): Finger[] | null => {
  const octave = fingeringFor(unitId, tonic);
  const fingers = hand === 'RH' ? octave?.rh : octave?.lh;
  return fingers ? runFingers(fingers, hand, rangeOctaves, unitId) : null;
};

/** A scale written from its (written) tonic, ready to lay out as a run. */
export interface WrittenScaleRun {
  /** The scale's notes spelled ascending from the written tonic (the first is the tonic). */
  scaleNotes: readonly SpelledNote[];
  keySignatureFifths: number;
  /** The walk's semitone steps over one octave. */
  intervals: readonly number[];
  config: ScaleConfig;
  /** Each hand's fingers along the ascending walk, where the reference has them. */
  fingers?: Partial<Record<RunHand, Finger[] | null>>;
  showFingering: boolean;
}

/**
 * Lay out a written scale as a run: notes, timing, register and staves, and the judged stream
 * that mirrors the notation, from one walk.
 */
export const writeScaleRun = (run: WrittenScaleRun): ExpandedRun => {
  const { config } = run;
  const [tonic] = run.scaleNotes;
  if (!tonic) throw new Error('OSMDExerciseScene.expander: a scale needs notes');
  const spelling = spellingByPitchClass(run.scaleNotes);
  const spell = (midi: number): { letter: NoteLetter; alteration: number } => {
    const scaleNote = spelling.get(pitchClassOf(midi));
    if (!scaleNote) {
      throw new Error(`OSMDExerciseScene.expander: ${midi} is not a note of the scale`);
    }
    return { letter: scaleNote.letter, alteration: scaleNote.alteration };
  };

  const offsets = ascendingOffsets([...run.intervals], config.range_octaves);
  const register = runRegister(tonic, config.range_octaves);
  const walk = directedIndexes(offsets.length, config.direction);
  const timing = runTiming(
    walk.length,
    RHYTHM_DIVISIONS[config.rhythm_density],
    config.direction === 'both',
  );
  const hands = handsToRender(config.hand);

  const handNotes = (hand: RunHand): RunScoreNote[] => {
    const base = hand === 'RH' ? register.rhBase : register.lhBase;
    const fingers = run.fingers?.[hand];
    return walk.map((ascendingIndex, step) => {
      const midi = base + offsets[ascendingIndex];
      const finger = fingers?.[ascendingIndex];
      return {
        midi,
        ...spell(midi),
        ...timing.notes[step],
        ...(finger !== undefined ? { finger } : {}),
      };
    });
  };
  const written = hands.reduce<Partial<Record<RunHand, RunScoreNote[]>>>(
    (all, hand) => ({ ...all, [hand]: handNotes(hand) }),
    {},
  );

  const score = writeRunScore({
    keySignatureFifths: run.keySignatureFifths,
    hands: written,
    tonic,
    timing,
    showFingering: run.showFingering,
  });

  // The judged stream: by onset, the right hand before the left at a shared onset.
  const judgedNotes: JudgedNote[] = walk
    .flatMap((_unused, step) =>
      hands.map((hand) => {
        const note = written[hand]![step];
        return {
          midiNotes: [note.midi],
          hand,
          staff: score.staves[hand]![step],
          startDivisions: note.startDivisions,
          durationDivisions: note.durationDivisions,
          ...(note.finger !== undefined ? { finger: note.finger } : {}),
        };
      }),
    )
    .map((note, index) => ({ index, ...note }));

  return {
    musicXml: score.musicXml,
    lines: score.lines,
    measureCount: score.measureCount,
    judgedNotes,
    timeSignature: timing.meter,
    register,
  };
};

/** The meter a run of this configuration is played in (a full bar's), before it is expanded. */
export const runMeterFor = (
  intervals: readonly number[],
  config: ScaleConfig,
): [number, number] => {
  const walk = directedIndexes(intervals.length * config.range_octaves + 1, config.direction);
  return runTiming(
    walk.length,
    RHYTHM_DIVISIONS[config.rhythm_density],
    config.direction === 'both',
  ).meter;
};

/**
 * Expand a `stepwise_run` take into notation + judged stream from one walk.
 */
export const expandStepwiseRun = (
  prompt: StepwiseRunPrompt,
  cell: ScaleCell,
  config: ScaleConfig,
  notationOptions: ExpanderNotationOptions = {},
): ExpandedRun => {
  const unitId = unitIdForScaleSlug(cell.scale);
  const notation = scaleNotation(
    unitId,
    cell.tonic,
    notationOptions.keySignature ?? DEFAULT_KEY_SIGNATURE_DISPLAY,
  );
  return writeScaleRun({
    scaleNotes: spellScale(unitId, notation.tonic),
    keySignatureFifths: notation.keySignatureFifths,
    intervals: prompt.intervals,
    config,
    fingers: {
      RH: ascendingFingers(unitId, cell.tonic, 'RH', config.range_octaves),
      LH: ascendingFingers(unitId, cell.tonic, 'LH', config.range_octaves),
    },
    showFingering: (notationOptions.fingerNumbers ?? DEFAULT_FINGER_NUMBERS_DISPLAY) === 'show',
  });
};
